// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useCart } from '../contexts/CartContext';
import { Search, SlidersHorizontal, ShoppingCart } from 'lucide-react';

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

function getProductEmoji(name = '') {
  const n = name.toLowerCase();
  if (n.includes('banana'))    return '🍌'; if (n.includes('apple'))  return '🍎';
  if (n.includes('spinach') || n.includes('lettuce')) return '🥬';
  if (n.includes('avocado'))  return '🥑';  if (n.includes('tomato')) return '🍅';
  if (n.includes('milk'))     return '🥛';  if (n.includes('egg'))    return '🥚';
  if (n.includes('yogurt'))   return '🫙';  if (n.includes('cheese')) return '🧀';
  if (n.includes('chicken'))  return '🍗';
  if (n.includes('beef') || n.includes('ground')) return '🥩';
  if (n.includes('salmon') || n.includes('seafood')) return '🐟';
  if (n.includes('bread') || n.includes('sourdough')) return '🍞';
  if (n.includes('croissant')) return '🥐'; if (n.includes('pasta'))  return '🍝';
  if (n.includes('sauce'))    return '🫙';  if (n.includes('olive'))  return '🫒';
  if (n.includes('water'))    return '💧';  if (n.includes('juice'))  return '🍊';
  if (n.includes('coffee'))   return '☕';
  return '🛒';
}

function ProductCard({ product, onAdd }) {
  const [adding, setAdding] = useState(false);
  const isOnSale = product.sale_price_cents && product.sale_price_cents < product.price_cents;
  const priceCents = isOnSale ? product.sale_price_cents : product.price_cents;

  const handleAdd = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setAdding(true);
    await onAdd(product);
    setTimeout(() => setAdding(false), 800);
  };

  return (
    <Link to={`/products/${product.id}`} style={{ textDecoration: 'none' }}>
      <div className="product-card" style={{ position: 'relative' }}>
        <div className="product-img" style={{ position: 'relative' }}>
          {product.is_organic && (
            <span style={{ position: 'absolute', top: 8, left: 8, background: '#16A34A', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, zIndex: 1 }}>ORGANIC</span>
          )}
          {product.is_local && !product.is_organic && (
            <span style={{ position: 'absolute', top: 8, left: 8, background: '#3B82F6', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, zIndex: 1 }}>LOCAL</span>
          )}
          {isOnSale && (
            <span style={{ position: 'absolute', top: 8, right: 8, background: '#EF4444', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, zIndex: 1 }}>SALE</span>
          )}
          <span style={{ fontSize: 56, zIndex: 0 }}>{getProductEmoji(product.name)}</span>
        </div>
        <div className="product-body">
          <div className="product-name">{product.name}</div>
          {product.description && <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.description}</div>}
          <div className="product-unit">{product.unit}</div>
          <div className="product-price-row">
            <div>
              {isOnSale ? (
                <>
                  <span className="product-sale-price">{fmt(product.sale_price_cents)}</span>
                  <span className="product-original-price">{fmt(product.price_cents)}</span>
                </>
              ) : (
                <span className="product-price">{fmt(product.price_cents)}</span>
              )}
            </div>
            <button onClick={handleAdd} disabled={adding || !product.in_stock} style={{
              width: 34, height: 34, borderRadius: 10,
              background: !product.in_stock ? '#E2E8F0' : adding ? '#86EFAC' : '#16A34A',
              border: 'none', cursor: product.in_stock ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0,
            }}>
              {adding ? <span style={{ color: '#fff', fontSize: 14, fontWeight: 800 }}>✓</span>
                      : <ShoppingCart size={16} color={product.in_stock ? '#fff' : '#94A3B8'} />}
            </button>
          </div>
          {!product.in_stock && (
            <div style={{ fontSize: 11, color: '#EF4444', fontWeight: 600, marginTop: 4 }}>Out of stock</div>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function ProductsPage() {
  const { slug }              = useParams();
  const [searchParams]        = useSearchParams();
  const { addItem }           = useCart();
  const navigate              = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState(searchParams.get('q') || '');
  const [filters, setFilters] = useState({ organic: false, local: false, onSale: false, inStock: true });
  const [sortBy, setSortBy]   = useState('featured');

  useEffect(() => {
    fetchData();
  }, [activeCategory, search, filters, sortBy]);

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const fetchData = async () => {
    setLoading(true);

    const [{ data: cats }] = await Promise.all([
      supabase.from('grocery_categories').select('*').eq('active', true).order('sort_order'),
    ]);
    setCategories(cats || []);

    let query = supabase.from('grocery_products').select('*, grocery_categories(name, slug)').eq('active', true);

    if (activeCategory && activeCategory !== 'all') {
      const cat = (cats || []).find(c => c.slug === activeCategory);
      if (cat) query = query.eq('category_id', cat.id);
    }
    if (search) query = query.ilike('name', `%${search}%`);
    if (filters.organic) query = query.eq('is_organic', true);
    if (filters.local)   query = query.eq('is_local', true);
    if (filters.onSale)  query = query.not('sale_price_cents', 'is', null);
    if (filters.inStock) query = query.eq('in_stock', true);

    if (sortBy === 'price_asc')  query = query.order('price_cents', { ascending: true });
    else if (sortBy === 'price_desc') query = query.order('price_cents', { ascending: false });
    else if (sortBy === 'name')  query = query.order('name');
    else query = query.order('is_featured', { ascending: false }).order('name');

    const { data } = await query;
    setProducts(data || []);
    setLoading(false);
  };

  const toggleFilter = (key) => setFilters(f => ({ ...f, [key]: !f[key] }));

  const catLabel = categories.find(c => c.slug === activeCategory)?.name || 'All Products';

  return (
    <div className="page-container" style={{ paddingTop: 24 }}>
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        {/* Sidebar */}
        <aside style={{ width: 220, flexShrink: 0, display: 'none' }} className="products-sidebar">
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Categories</div>
            <div
              onClick={() => { setActiveCategory('all'); navigate('/products'); }}
              style={{ padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: activeCategory === 'all' ? 700 : 500, color: activeCategory === 'all' ? '#16A34A' : '#64748B', background: activeCategory === 'all' ? '#F0FDF4' : 'transparent' }}
            >
              All Products
            </div>
            {categories.map(cat => (
              <div
                key={cat.id}
                onClick={() => { setActiveCategory(cat.slug); navigate(`/categories/${cat.slug}`); }}
                style={{ padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: activeCategory === cat.slug ? 700 : 500, color: activeCategory === cat.slug ? '#16A34A' : '#64748B', background: activeCategory === cat.slug ? '#F0FDF4' : 'transparent' }}
              >
                {cat.name}
              </div>
            ))}
          </div>
        </aside>

        {/* Main */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Header row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#14532D', margin: 0 }}>{catLabel}</h1>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#94A3B8' }}>{products.length} products</span>
              <select
                className="input"
                style={{ width: 'auto', minHeight: 36, padding: '6px 12px', fontSize: 13 }}
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
              >
                <option value="featured">Featured</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="name">Name A-Z</option>
              </select>
            </div>
          </div>

          {/* Search + filters */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 360 }}>
              <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', pointerEvents: 'none' }} />
              <input
                className="input"
                style={{ paddingLeft: 36, minHeight: 38 }}
                placeholder="Search products..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            {[
              { key: 'organic', label: '🌱 Organic' },
              { key: 'local',   label: '📍 Local' },
              { key: 'onSale',  label: '🏷️ On Sale' },
              { key: 'inStock', label: 'In Stock' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => toggleFilter(f.key)}
                style={{
                  padding: '7px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  border: '1.5px solid',
                  borderColor: filters[f.key] ? '#16A34A' : '#BBF7D0',
                  background: filters[f.key] ? '#16A34A' : '#fff',
                  color: filters[f.key] ? '#fff' : '#64748B',
                  transition: 'all 0.15s',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Category pills (mobile-friendly) */}
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8, marginBottom: 16 }}>
            <button
              onClick={() => { setActiveCategory('all'); navigate('/products'); }}
              style={{
                padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: 'none', flexShrink: 0,
                background: activeCategory === 'all' ? '#16A34A' : '#F0FDF4',
                color: activeCategory === 'all' ? '#fff' : '#64748B',
              }}
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => { setActiveCategory(cat.slug); navigate(`/categories/${cat.slug}`); }}
                style={{
                  padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  border: 'none', flexShrink: 0,
                  background: activeCategory === cat.slug ? '#16A34A' : '#F0FDF4',
                  color: activeCategory === cat.slug ? '#fff' : '#64748B',
                }}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: '#94A3B8' }}>
              <div className="spinner" style={{ margin: '0 auto 16px' }} />
              Loading products...
            </div>
          ) : products.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: '#94A3B8' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No products found</div>
              <div style={{ fontSize: 14 }}>Try adjusting your filters or search.</div>
            </div>
          ) : (
            <div className="product-grid">
              {products.map(prod => (
                <ProductCard key={prod.id} product={prod} onAdd={addItem} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
